import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Plus, Trash2, ArrowUp, ArrowDown, Save } from "lucide-react";
import { toast } from "sonner";
import client from "@/api/client";
import { useSiteContent } from "@/context/SiteContentContext";

const TYPES = ["text", "email", "tel", "url", "number", "textarea", "select", "checkbox"];

const BLANK = {
  id: "", type: "text", required: false,
  label_en: "", label_ar: "",
  placeholder_en: "", placeholder_ar: "",
  options_en: [], options_ar: [],
};

function slug(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40);
}

export function AdminWaitlistFormBuilder() {
  const { t } = useTranslation();
  const { content, refresh } = useSiteContent();
  const [schema, setSchema] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (content?.waitlist_form_schema) setSchema(JSON.parse(JSON.stringify(content.waitlist_form_schema)));
  }, [content]);

  if (!schema) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const setField = (idx, patch) => setSchema((s) => {
    const fields = [...(s.fields || [])];
    fields[idx] = { ...fields[idx], ...patch };
    return { ...s, fields };
  });
  const move = (idx, dir) => setSchema((s) => {
    const fields = [...(s.fields || [])];
    const j = idx + dir;
    if (j < 0 || j >= fields.length) return s;
    [fields[idx], fields[j]] = [fields[j], fields[idx]];
    return { ...s, fields };
  });
  const remove = (idx) => setSchema((s) => {
    const f = s.fields[idx];
    if (f?.core) { toast.error("Core fields (name/email/etc) can't be deleted — only reordered or renamed."); return s; }
    return { ...s, fields: s.fields.filter((_, i) => i !== idx) };
  });
  const add = () => setSchema((s) => ({ ...s, fields: [...(s.fields || []), { ...BLANK, id: `custom_${Date.now()}`, label_en: "New field", label_ar: "حقل جديد" }] }));

  const save = async () => {
    setSaving(true);
    try {
      const clean = { ...schema, fields: (schema.fields || []).map((f) => ({
        ...f,
        id: f.id || slug(f.label_en) || `field_${Date.now()}`,
        options_en: f.type === "select" ? (f.options_en || []).filter(Boolean) : undefined,
        options_ar: f.type === "select" ? (f.options_ar || []).filter(Boolean) : undefined,
      })) };
      await client.put("/admin/content", { waitlist_form_schema: clean });
      await refresh();
      toast.success(t("admin.saved"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.error")); } finally { setSaving(false); }
  };

  return (
    <Card className="rounded-2xl border border-border p-6 space-y-6" data-testid="admin-panel-waitlist-form">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold">{t("admin.waitlistForm")}</h2>
          <p className="text-sm text-muted-foreground mt-1">{t("admin.waitlistFormDesc")}</p>
        </div>
        <Button className="btn-brand rounded-xl gap-2" onClick={save} disabled={saving} data-testid="admin-waitlist-form-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {t("common.save")}
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>{t("admin.introText")} (EN)</Label>
          <Textarea value={schema.intro_en || ""} onChange={(e) => setSchema({ ...schema, intro_en: e.target.value })} className="mt-1.5 rounded-xl min-h-20" />
        </div>
        <div>
          <Label>{t("admin.introText")} (AR)</Label>
          <Textarea dir="rtl" value={schema.intro_ar || ""} onChange={(e) => setSchema({ ...schema, intro_ar: e.target.value })} className="mt-1.5 rounded-xl min-h-20" />
        </div>
      </div>

      <Separator />

      <div className="space-y-3">
        {(schema.fields || []).map((f, idx) => (
          <div key={f.id + idx} className="rounded-xl border border-border p-4 space-y-3" data-testid="admin-waitlist-field-row">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-muted-foreground">#{idx + 1}</span>
                <span className="font-medium">{f.label_en || f.id}</span>
                {f.core && <span className="pill !text-[10px]">core</span>}
              </div>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="ghost" onClick={() => move(idx, -1)} disabled={idx === 0} title="Move up"><ArrowUp className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => move(idx, 1)} disabled={idx === schema.fields.length - 1} title="Move down"><ArrowDown className="h-4 w-4" /></Button>
                {!f.core && (
                  <Button size="icon" variant="ghost" onClick={() => remove(idx)} data-testid="admin-waitlist-field-delete"><Trash2 className="h-4 w-4 text-destructive" /></Button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">ID</Label>
                <Input value={f.id} onChange={(e) => setField(idx, { id: slug(e.target.value) })} disabled={!!f.core} className="mt-1 rounded-lg h-9 font-mono text-xs" />
              </div>
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={f.type} onValueChange={(v) => setField(idx, { type: v })}>
                  <SelectTrigger className="mt-1 rounded-lg h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((tp) => <SelectItem key={tp} value={tp}>{tp}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Label (EN)</Label>
                <Input value={f.label_en || ""} onChange={(e) => setField(idx, { label_en: e.target.value })} className="mt-1 rounded-lg h-9" />
              </div>
              <div>
                <Label className="text-xs">Label (AR)</Label>
                <Input dir="rtl" value={f.label_ar || ""} onChange={(e) => setField(idx, { label_ar: e.target.value })} className="mt-1 rounded-lg h-9" />
              </div>
              {f.type !== "checkbox" && f.type !== "select" && (<>
                <div><Label className="text-xs">Placeholder (EN)</Label><Input value={f.placeholder_en || ""} onChange={(e) => setField(idx, { placeholder_en: e.target.value })} className="mt-1 rounded-lg h-9" /></div>
                <div><Label className="text-xs">Placeholder (AR)</Label><Input dir="rtl" value={f.placeholder_ar || ""} onChange={(e) => setField(idx, { placeholder_ar: e.target.value })} className="mt-1 rounded-lg h-9" /></div>
              </>)}
              {f.type === "select" && (<>
                <div>
                  <Label className="text-xs">Options (EN) — one per line</Label>
                  <Textarea value={(f.options_en || []).join("\n")} onChange={(e) => setField(idx, { options_en: e.target.value.split("\n") })} className="mt-1 rounded-lg" />
                </div>
                <div>
                  <Label className="text-xs">Options (AR) — one per line</Label>
                  <Textarea dir="rtl" value={(f.options_ar || []).join("\n")} onChange={(e) => setField(idx, { options_ar: e.target.value.split("\n") })} className="mt-1 rounded-lg" />
                </div>
              </>)}
              <div className="flex items-center gap-2">
                <Switch id={`req-${idx}`} checked={!!f.required} onCheckedChange={(v) => setField(idx, { required: v })} />
                <Label htmlFor={`req-${idx}`} className="cursor-pointer text-xs">Required</Label>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Button variant="outline" className="rounded-xl gap-2" onClick={add} data-testid="admin-waitlist-field-add"><Plus className="h-4 w-4" /> {t("admin.addField")}</Button>
    </Card>
  );
}
